import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, Mail, MessageSquare } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "../lib/api";
import { setMeta } from "../lib/seo";

const FAQ = [
  { q: "Do you sell products directly?", a: "No. ShoppinMagic is an affiliate marketplace. All orders, payments, shipping, and returns are handled by the external merchants we link to." },
  { q: "How do I get a refund or return an item?", a: "Contact the merchant you purchased from directly, using your order confirmation email. See our Refund Policy for step-by-step instructions." },
  { q: "Do affiliate links cost me more?", a: "Never. The price is identical whether you use our link or visit the merchant directly. Commissions come from the merchant's margin." },
  { q: "How do I unsubscribe from the newsletter?", a: "Every email includes an unsubscribe link. You can also email us and we'll remove you within 48 hours." },
  { q: "I'm a merchant — how can I list products?", a: "Head to our Sell on ShoppinMagic page and submit the onboarding form. We review applications within 3–5 business days." },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "General question", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setMeta("Contact Us | ShoppinMagic", "Questions, feedback, or partnership inquiries — get in touch with the ShoppinMagic team.");
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please fill in your name and message.");
      return;
    }
    setLoading(true);
    try {
      await api.contact(form);
      toast.success("Message sent! We typically reply within 1–2 business days.");
      setForm({ name: "", email: "", subject: "General question", message: "" });
    } catch {
      toast.error("Couldn't send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="grid gap-10 lg:grid-cols-2">
        <div>
          <span className="text-xs font-bold uppercase tracking-wider text-gold-600">Contact</span>
          <h1 className="font-heading mt-2 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">We'd love to hear from you</h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600 sm:text-base">
            Product suggestions, corrections, partnership ideas, or a deal we missed — send it over.
          </p>
          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <Mail size={18} className="text-gold-600" />
              <div>
                <p className="text-sm font-bold text-slate-900">Email</p>
                <p className="text-sm text-slate-500" data-testid="contact-email">therealcoman@yahoo.com</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <Clock size={18} className="text-gold-600" />
              <div>
                <p className="text-sm font-bold text-slate-900">Response time</p>
                <p className="text-sm text-slate-500">Usually within 1–2 business days</p>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h2 className="font-heading flex items-center gap-2 text-lg font-bold text-slate-900">
              <MessageSquare size={18} className="text-gold-600" /> Quick answers
            </h2>
            <Accordion type="single" collapsible className="mt-3" data-testid="contact-faq">
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="rounded-xl border border-slate-200 bg-white px-4 mb-2 shadow-sm">
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-sm font-semibold text-slate-800">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-slate-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="h-fit rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <h2 className="font-heading text-xl font-bold text-slate-900">Send a message</h2>
          <div className="mt-5 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-1 block text-xs font-bold text-slate-700">Name</label>
                <input id="contact-name" value={form.name} onChange={set("name")} required data-testid="contact-name-input"
                  className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-200" placeholder="Your name" />
              </div>
              <div>
                <label htmlFor="contact-email-input" className="mb-1 block text-xs font-bold text-slate-700">Email</label>
                <input id="contact-email-input" type="email" value={form.email} onChange={set("email")} required data-testid="contact-email-input"
                  className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-200" placeholder="you@example.com" />
              </div>
            </div>
            <div>
              <label htmlFor="contact-subject" className="mb-1 block text-xs font-bold text-slate-700">Subject</label>
              <select id="contact-subject" value={form.subject} onChange={set("subject")} data-testid="contact-subject-select"
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm focus:border-gold-500 focus:outline-none">
                <option>General question</option>
                <option>Product suggestion</option>
                <option>Report an issue</option>
                <option>Merchant / partnership</option>
                <option>Privacy request (GDPR/CCPA)</option>
              </select>
            </div>
            <div>
              <label htmlFor="contact-message" className="mb-1 block text-xs font-bold text-slate-700">Message</label>
              <textarea id="contact-message" value={form.message} onChange={set("message")} required rows={5} data-testid="contact-message-input"
                className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-200" placeholder="How can we help?" />
            </div>
            <button type="submit" disabled={loading} data-testid="contact-submit-button"
              className="w-full rounded-xl bg-gold-600 py-3.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-gold-700 disabled:opacity-60">
              {loading ? "Sending…" : "Send message"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
